/**
 * Shared form-control styles: Input, Textarea, Select trigger, Combobox trigger.
 * Not Checkbox / RadioGroup / Switch / Slider; label text is the `label` tier.
 *
 * State constants ship their selector baked in — they carry disabled-gating and
 * specificity decisions. Where a control's hovered element differs from the one
 * it paints, there is a `*Within` twin.
 *
 * @see ../../../../.ai-context/design-system/token-taxonomy.md
 */

import { cn } from '@heidi/utils';

// Deep import, not the `../typography` door: this module evaluates inside any
// app test that renders a control while partially mocking that door, and a
// factory omitting `typographyVariant` would leave it `undefined` here.
import { typographyVariant } from '../typography/typography.styles';

/** The value a control holds — 14/400, lighter than a Button label. */
export const controlValueText = cn(
  typographyVariant({ variant: 'p2' }),
  'text-control-text-value'
);

/**
 * The value a **compact** control holds — 12/400. Density moves the step, never
 * the weight: at 500 a compact field would match the `size="xs"` Button beside
 * it (`captionBold`, 12/500) and take its placeholder up with it. 12px is
 * deliberate — it's the ladder that has to survive density, not the size.
 */
export const controlValueTextCompact = cn(
  typographyVariant({ variant: 'caption' }),
  'text-control-text-value'
);

/**
 * Placeholder and disabled roles for a native field: their tints, plus the
 * placeholder's weight. Select and Combobox can't use it — their placeholder is
 * a `data-[placeholder]` or a span.
 *
 * The weight is stated here rather than left to the field: `::placeholder`
 * inherits `font-weight`, so an unstated one lets a density variant re-bold it.
 */
export const controlFieldStateText =
  'placeholder:font-normal placeholder:text-control-text-placeholder disabled:text-control-text-value-disabled disabled:placeholder:text-control-text-placeholder-disabled';

/** The whole text role for a single-density native field. */
export const controlFieldText = cn(controlValueText, controlFieldStateText);

/** Single-line height, matching the experimental Button's `size="md"`. Textarea is exempt. */
export const controlHeight = 'h-9';

/** The compact box — 28px, matching Button `size="xs"`. Bordered element, like {@link controlHeight}. */
export const controlHeightCompact = 'h-7';

/** The field box at rest. Radius and padding stay with the recipe. */
export const controlSurface =
  'border border-solid border-control-border bg-control-background';

/** The only role that fades. Asserted composited in `themes.test.ts`. */
export const CONTROL_ICON_REST_ALPHA = 0.5;

/**
 * The chevron a Select or Combobox trigger renders. Direct child only: a
 * descendant selector would also catch consumer icons in accessory slots, which
 * are not the tier's to colour. Carries its own transition — `transition` is not
 * inherited.
 */
export const controlChevron =
  '[&>svg]:text-control-icon/50 [&>svg]:transition-colors [&>svg]:duration-150';

/** The disabled box, unprefixed — Input's wrapper is a `<div>`, never `:disabled`. */
export const controlSurfaceDisabled =
  'border-control-border-disabled bg-control-background-disabled';

/**
 * Colours and the focus halo, 150ms.
 *
 * The property list is `transition-colors`' own plus `box-shadow`, spelled out
 * because that shorthand omits box-shadow and a ring *is* a box-shadow — without
 * it the halo snaps in while the border under it fades. Never `transition-all`.
 *
 * `::placeholder` is armed separately: a pseudo-element doesn't inherit the
 * transition. `duration-150` is redundant there but stated so the Combobox
 * trigger displaces the Button's — property and duration are separate
 * tailwind-merge groups.
 */
export const controlTransition = [
  'transition-[color,background-color,border-color,text-decoration-color,fill,stroke,box-shadow] duration-150',
  'placeholder:transition-colors placeholder:duration-150',
].join(' ');

/**
 * Border and glyphs on interaction, for a control that is itself the disableable
 * element — Textarea, the Select and Combobox triggers.
 *
 * `enabled:` is load-bearing: it and the `disabled:` rule a recipe pairs it with
 * are both single-class selectors, so otherwise a disabled-and-hovered control
 * resolves on Tailwind's emission order.
 */
export const controlSurfaceHover = [
  'enabled:hover:border-control-border-hover enabled:active:border-control-border-hover',
  // `[&>svg]` must lead: Tailwind applies variants right-to-left, so
  // `enabled:hover:[&>svg]:x` compiles to `.cls>svg:hover:enabled` — pseudo-classes
  // on the glyph, which is never `:enabled`, so the rule can never match.
  '[&>svg]:enabled:hover:text-control-icon [&>svg]:enabled:active:text-control-icon',
  '[&>svg]:focus-visible:text-control-icon',
].join(' ');

/**
 * {@link controlSurfaceHover} for Input, whose bordered element is the `<div>`
 * wrapper. A `<div>` is never `:enabled`, so the gate reads the field inside.
 * Border only — Input renders no chevron.
 *
 * `input:enabled`, not a bare `:enabled` — the wrapper also holds accessory
 * buttons, and `:has(:enabled)` would light the border on a disabled field.
 */
export const controlSurfaceHoverWithin =
  'has-[input:enabled]:hover:border-control-border-hover has-[input:enabled]:active:border-control-border-hover';

/**
 * Focus — a full-strength border swap plus a 1.5px halo of the same token at
 * `/60`. Same shape as the Button's focus ring, not the same colour.
 *
 * `/60`, not `/50`: at 1.5px against an opaque line, `/50` reads as a gap
 * between the line and the page rather than a continuation of it.
 *
 * `z-10` protects the halo — a ring is a box-shadow painted at the element's
 * stacking position, so a later-painting sibling (abutting fields, segmented
 * rows) would cover it. Needs a flex/grid item or a positioned element.
 *
 * Restated under `enabled:hover:` so focus wins the overlap on specificity
 * (0,4,0 over 0,3,0) rather than on Tailwind's variant sort order.
 */
export const controlFocus = [
  'outline-none focus-visible:outline-none',
  'focus-visible:z-10',
  'focus-visible:border-control-border-focus',
  'focus-visible:ring-[1.5px] focus-visible:ring-control-border-focus/60',
  'enabled:hover:focus-visible:border-control-border-focus',
].join(' ');

/** {@link controlFocus} keyed to a descendant field — Input's `<div>` wrapper. */
export const controlFocusWithin = [
  'outline-none',
  'has-[:focus-visible]:z-10',
  'has-[:focus-visible]:border-control-border-focus',
  'has-[:focus-visible]:ring-[1.5px] has-[:focus-visible]:ring-control-border-focus/60',
  'has-[input:enabled]:hover:has-[:focus-visible]:border-control-border-focus',
].join(' ');

/**
 * Invalid — **a border colour, and nothing else**, keyed to `aria-invalid="true"`.
 *
 * **The halo belongs to focus, not to invalid.** Validity picks the boundary's
 * colour; focus decides whether there is a halo around it. So this recipe never
 * sets a ring width — an unfocused invalid field is a red border on a plain
 * field, and on a focused one {@link controlFocus} supplies the width while this
 * supplies the colour.
 *
 * The ladder mirrors the neutral one, since a red field still answers the
 * pointer: rest and focus hold `control-border-invalid`, hover and active step
 * to `control-border-invalid-hover`.
 *
 * Each rule stacks `aria-[invalid='true']:` on top of the same conditions as the
 * neutral rule it has to beat, so the outcome is decided on specificity rather
 * than on Tailwind's emission order. Without that, hovering an invalid field
 * falls through to `control-border-hover` and drops the error signal.
 *
 * Not `focusRingInvalidExperimental`: it draws a halo on an unfocused field, and
 * it keys the attribute on the element it styles, which Input can't do — hence
 * the {@link controlInvalidWithin} twin.
 */
export const controlInvalid = [
  "aria-[invalid='true']:border-control-border-invalid",
  "aria-[invalid='true']:enabled:hover:border-control-border-invalid-hover",
  "aria-[invalid='true']:enabled:active:border-control-border-invalid-hover",
  "aria-[invalid='true']:focus-visible:border-control-border-invalid",
  "aria-[invalid='true']:focus-visible:ring-control-border-invalid/60",
  "aria-[invalid='true']:enabled:hover:focus-visible:border-control-border-invalid",
  "aria-[invalid='true']:enabled:hover:focus-visible:ring-control-border-invalid/60",
].join(' ');

/**
 * {@link controlInvalid} keyed to a descendant field — Input's `<div>` wrapper.
 *
 * Input paints the wrapper but spreads `aria-invalid` onto the `<input>` inside
 * it, so the attribute and the border are never on the same element. Scoped to
 * `input[aria-invalid]` rather than a bare `[aria-invalid='true']` for the same
 * reason {@link controlSurfaceHoverWithin} scopes to `input:enabled` — an
 * accessory carrying its own `aria-invalid` must not paint the field's border.
 */
export const controlInvalidWithin = [
  "has-[input[aria-invalid='true']]:border-control-border-invalid",
  "has-[input[aria-invalid='true']]:has-[input:enabled]:hover:border-control-border-invalid-hover",
  "has-[input[aria-invalid='true']]:has-[input:enabled]:active:border-control-border-invalid-hover",
  "has-[input[aria-invalid='true']]:has-[:focus-visible]:border-control-border-invalid",
  "has-[input[aria-invalid='true']]:has-[:focus-visible]:ring-control-border-invalid/60",
  "has-[input[aria-invalid='true']]:has-[input:enabled]:hover:has-[:focus-visible]:border-control-border-invalid",
  "has-[input[aria-invalid='true']]:has-[input:enabled]:hover:has-[:focus-visible]:ring-control-border-invalid/60",
].join(' ');
